/**
 * @file    widgetSerializer.ts
 * @scope   SHARED
 * @summary Converts the in-memory `EnterpriseGlobalModel` (what the builder UI edits) into the
 *          persisted widget `CustomConfig` (what the backend/renderer expect).
 * @consumes EnterpriseGlobalModel, calculationPayload mappers, Trend
 * @produces `extractEnterpriseDataToWidget`, Enterprise serializer-delta hooks
 *           (`getEnterpriseCollections`, `serializeEnterpriseCollection`)
 * @sideEffects None on state — returns the (mutated) config object, as before.
 * @notes Inverse of `widgetDeserializer.ts`; keep the two in sync when the payload shape changes.
 *        Phase 2: collection identity/originId/edit-mode lookup live in the engine core
 *        (`engine/serialization/baseSerializer`); this file only owns the Enterprise body.
 */
import { EnterpriseGlobalModel } from 'models/Enterprise/EnterpriseGlobalModel';
import { SelectedUserFields } from 'models/Enterprise/FieldsManagerModel';
import { CustomConfig } from 'models/WidgetModel';
import type { CollectionRef, CollectionSerializeContext } from '../../../../engine/contracts';
import { serializeWithDelta } from '../../../../engine/serialization/baseSerializer';
import {
  mapSelectedFieldToQueryCollectionField,
  buildFilterGroup,
} from '../payload/calculationPayload';
import { Trend } from 'core-ui/SelectTrendInput/SelectTrendInput';

/**
 * Builds a PartitionFilter from the partition-level filters configured via
 * PartitionFiltersManager (independent of each field's own filters).
 * @param associatedFields - The selected fields/filters for a partition
 * @returns A FilterGroup object or undefined if no partition filter is configured
 */
const buildPartitionFilter = (
  associatedFields: SelectedUserFields | undefined
): Record<string, unknown> | undefined => {
  const filters = associatedFields?.partitionFilters;
  if (!filters || filters.length === 0) return undefined;

  // Half-configured rows (added in the UI but never completed) must not reach the payload.
  // The check is intentionally based on attributePath instead of filterGuid so that
  // not-yet-recovered edit-mode stubs still round-trip correctly.
  const completeFilters = filters.filter(
    (filter) =>
      !!(filter.attributePath || filter.filterName) &&
      !!filter.operator &&
      filter.selectedValues.length > 0
  );

  return buildFilterGroup(completeFilters);
};

/**
 * Collections the Enterprise subtype persists: one per selected partition.
 * @param slice - The enterprise state
 * @returns Collection identities in partition order
 */
export const getEnterpriseCollections = (slice: EnterpriseGlobalModel): CollectionRef[] =>
  (slice.selectedValues.selectedPartitions ?? []).map((partition) => ({
    guid: partition.value,
    originId: partition.originId,
  }));

/**
 * Enterprise-owned body of one collection: fields, partitionFilter and trend placement.
 * Identity keys (`dataCollectionGuid`, `originId`) are added by the engine core.
 * @param slice - The enterprise state
 * @param ref - The collection being serialized
 * @param context - Previously persisted collection + selected trend
 * @returns The collection body to merge after the identity keys
 */
export const serializeEnterpriseCollection = (
  slice: EnterpriseGlobalModel,
  ref: CollectionRef,
  { originalCollection, trend }: CollectionSerializeContext
): Record<string, unknown> => {
  const associatedFields = slice.selectedValues.selectedEnterpriseFields.find(
    (field: any) => field.partitionGuid === ref.guid
  );

  // WHY: when fields come from edit mode and the user didn't visit FieldsManager, preserve the
  // original stored fields instead of re-building from lossy stubs.
  const fields =
    associatedFields?.fromEditMode && originalCollection && 'fields' in originalCollection
      ? originalCollection.fields
      : (associatedFields?.fields ?? []).map(mapSelectedFieldToQueryCollectionField);

  // Preserve previously saved trend, or store newly selected trend beside Fields.
  const originalTrend = originalCollection?.trend;

  const partitionFilter = buildPartitionFilter(associatedFields);

  return {
    fields: fields,
    ...(partitionFilter ? { partitionFilter: partitionFilter } : {}),
    ...(trend
      ? { trend: trend }
      : originalTrend
      ? { customWidgetConfig: { trend: originalTrend } }
      : {}),
  };
};

/**
 * Parses EnterpriseOptions and updates CustomWidgetConfig with enterprise-specific data.
 * Outputs the canonical camelCase format.
 * @param customWidgetConfig - The custom widget configuration to update
 * @param enterpriseGlobalObj - The enterprise options containing selected partitions and fields
 * @param trend - Optional trend data to include in queryCollections
 * @param availableCollections - Optional GuardRails collections to resolve missing originIds
 * @returns Updated CustomConfig with parsed enterprise data
 */
export const extractEnterpriseDataToWidget = (
  customWidgetConfig: CustomConfig | undefined,
  enterpriseGlobalObj: EnterpriseGlobalModel,
  trend?: Trend,
  availableCollections?: any[]
): CustomConfig | undefined =>
  serializeWithDelta(
    customWidgetConfig,
    enterpriseGlobalObj,
    {
      getCollections: getEnterpriseCollections,
      serializeCollection: serializeEnterpriseCollection,
    },
    { trend, availableCollections }
  );
